import PropTypes from 'prop-types';
import { Redirect, Route, Switch } from 'react-router-dom';
import { IfPermission } from '@folio/stripes-core';
import ChooseSourceRoute from './routes/ChooseSourceRoute';
import DataProvider from './providers/DataProvider';
import TemplatesListRoute from './routes/TemplatesListRoute';
import ViewTemplateRoute from './routes/ViewTemplateRoute';
import CustomFieldsRoute from './routes/CustomFieldsRoute';
import CustomFieldsManager from './components/Settings/CustomFields/components/CustomFieldsEdit';
import DefaultFieldsRoute from './routes/DefaultFieldsRoute';
import DefaultFieldsManager from './components/Settings/DefaultFields/components/DefaultFieldsEdit';
import HelpeProvider from './providers/HelpeProvider';
import OtherSettings from './components/Settings/OtherSettings';
import BarcodeLastIndexProvider from './providers/BarcodeLastIndexProvider';
import CustomFieldCategoryEdit from './components/Settings/CustomFields/components/Categories';
import BarcodeGeneralSettings from './components/Settings/Barcode/BarcodeGeneralSettings';
import BarcodePatternsSettings from './components/Settings/Barcode/BarcodePatternsSettings';
import CreateTemplate from './components/Settings/Templates/CreateTemplate';
import EditTemplate from './components/Settings/Templates/EditTemplate';
import DuplicateTemplate from './components/Settings/Templates/DuplicateTemplate';
import CreateInstance from './components/App/CreateInstance';
import EditInstance from './components/App/EditInstance';
import { FormPageErrorsProvider } from './providers/FormPagesErrorsProvider';
import { BarcodeSessionProvider } from './contexts/BarcodeSessionContext';
import PrintBarcodeRoute from './routes/PrintBarcodeRoute';
import SafeDeleteInstanceRoute from './routes/SafeDeleteInstanceRoute';
import BulkSafeDeleteRoute from './routes/BulkSafeDeleteRoute';
import BulkEditInstancesRoute from './routes/BulkEditInstancesRoute';
import {
  ArabicLanguageSettings,
  SearchFeaturesSettings,
  ReindexManagement,
} from './components/Settings/SearchIndexing';

function InventoryTemplatesPlugin({ basePath, redirectTo }) {
  return (
    <IfPermission perm="ui-plugin-inventory-templates.inventoryTemplates.view">
      <DataProvider>
        <FormPageErrorsProvider>
          <HelpeProvider>
            <BarcodeLastIndexProvider>
              <BarcodeSessionProvider>
                <Switch>
                  {redirectTo && (
                    <Redirect
                      exact
                      from={basePath}
                      to={`${basePath}/${redirectTo}`}
                    />
                  )}
                  <Route
                    path={`${basePath}/choose-source`}
                    render={() => <ChooseSourceRoute basePath={basePath} />}
                  />
                  <Route
                    path={`${basePath}/create/:templateId`}
                    render={() => <CreateInstance basePath={basePath} />}
                  />
                  <Route
                    path={`${basePath}/edit`}
                    render={() => <EditInstance basePath={basePath} />}
                  />
                  <Route
                    path={`${basePath}/list-templates`}
                    render={() => <TemplatesListRoute basePath={basePath} />}
                  />
                  <Route
                    path={`${basePath}/template-builder/create`}
                    render={() => <CreateTemplate basePath={basePath} />}
                  />
                  <Route
                    path={`${basePath}/template-builder/:templateId/edit`}
                    render={() => <EditTemplate basePath={basePath} />}
                  />
                  <Route
                    path={`${basePath}/template-builder/:templateId/duplicate`}
                    render={() => <DuplicateTemplate basePath={basePath} />}
                  />
                  <Route
                    path={`${basePath}/:templateId/view`}
                    render={() => <ViewTemplateRoute basePath={basePath} />}
                  />
                  <Route
                    path={`${basePath}/default-fields-list`}
                    render={() => <DefaultFieldsRoute basePath={basePath} />}
                  />
                  <Route
                    path={`${basePath}/defaultfields/:defaultFieldId/edit`}
                    render={() => <DefaultFieldsManager basePath={basePath} />}
                  />

                  <Route
                    path={`${basePath}/custom-fields-list`}
                    render={() => <CustomFieldsRoute basePath={basePath} />}
                  />
                  <Route
                    path={`${basePath}/customfields/create`}
                    render={() => <CustomFieldsManager basePath={basePath} />}
                  />
                  <Route
                    path={`${basePath}/customfields/:customFieldId/edit`}
                    render={() => <CustomFieldsManager basePath={basePath} />}
                  />

                  <Route
                    path={`${basePath}/category/create`}
                    render={() => (
                      <CustomFieldCategoryEdit basePath={basePath} />
                    )}
                  />
                  <Route
                    path={`${basePath}/category/:categoryId/edit`}
                    render={() => (
                      <CustomFieldCategoryEdit basePath={basePath} />
                    )}
                  />

                  <Route
                    path={`${basePath}/general-settings`}
                    render={() => (
                      <BarcodeGeneralSettings basePath={basePath} />
                    )}
                  />

                  <Route
                    path={`${basePath}/barcode-patterns`}
                    render={() => (
                      <BarcodePatternsSettings basePath={basePath} />
                    )}
                  />

                  <Route
                    path={`${basePath}/other-settings`}
                    render={() => <OtherSettings basePath={basePath} />}
                  />

                  <Route
                    path={`${basePath}/search-indexing/arabic-language`}
                    render={() => (
                      <ArabicLanguageSettings basePath={basePath} />
                    )}
                  />
                  <Route
                    path={`${basePath}/search-indexing/search-features`}
                    render={() => (
                      <SearchFeaturesSettings basePath={basePath} />
                    )}
                  />
                  <Route
                    path={`${basePath}/search-indexing/reindex-management`}
                    render={() => <ReindexManagement basePath={basePath} />}
                  />

                  <Route
                    path={`${basePath}/:instanceId/single`}
                    render={() => <PrintBarcodeRoute basePath={basePath} />}
                  />
                  <Route
                    path={`${basePath}/bulk-edit`}
                    render={() => <BulkEditInstancesRoute basePath={basePath} />}
                  />
                  <Route
                    path={`${basePath}/bulk`}
                    render={() => <BulkSafeDeleteRoute basePath={basePath} />}
                  />
                  <Route
                    path={`${basePath}/:instanceId`}
                    render={() => (
                      <SafeDeleteInstanceRoute basePath={basePath} />
                    )}
                  />
                </Switch>
              </BarcodeSessionProvider>
            </BarcodeLastIndexProvider>
          </HelpeProvider>
        </FormPageErrorsProvider>
      </DataProvider>
    </IfPermission>
  );
}

InventoryTemplatesPlugin.propTypes = {
  basePath: PropTypes.string,
  redirectTo: PropTypes.string,
};

export default InventoryTemplatesPlugin;
